import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import {
  Plus,
  Search,
  Users,
  Building,
  Mail,
  Phone,
  Briefcase,
  User,
  ExternalLink,
  Edit3,
  Trash2
} from 'lucide-react';
import AddExternalContactModal from './AddExternalContactModal';
import EditExternalContactModal from './EditExternalContactModal';

const ContactList = () => {
  const { user } = useAuth(); // Use the auth context instead of separate API call
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filter and search state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSource, setSelectedSource] = useState('all');
  
  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);

  useEffect(() => {
    loadContacts();
  }, []);

  // Remove the loadCurrentUser function since we're using useAuth now

  useEffect(() => {
    filterContacts();
  }, [contacts, searchTerm, selectedSource]);

  const loadContacts = async () => {
    try {
      setLoading(true);
      
      // Load both executives and external contacts
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      
      const [executivesResponse, contactsResponse] = await Promise.all([
        axios.get(`${apiBase}/executives`, { headers }).catch(err => {
          console.warn('Failed to load executives:', err);
          return { data: [] };
        }),
        axios.get(`${apiBase}/contacts`, { headers }).catch(err => {
          console.warn('Failed to load external contacts:', err);
          return { data: [] };
        })
      ]);
      
      // Convert executives to contact format and combine with external contacts
      const executivesAsContacts = (executivesResponse.data || []).map(exec => ({
        id: exec.id,
        full_name: exec.full_name || `${exec.first_name} ${exec.last_name}`,
        email: exec.email,
        title: exec.company_title || '',  // Use company_title (CEO, VP, etc.) not honorific
        honorific: exec.honorific || exec.title || '',  // Store honorific separately
        company: exec.company,
        primary_phone: exec.phone,
        source: 'executive', // Mark as executive to match dropdown value
        contact_type: 'executive',
        first_name: exec.first_name,
        last_name: exec.last_name
      }));
      
      // External contacts keep their existing structure
      const externalContacts = (contactsResponse.data || []).map(contact => ({
        ...contact,
        source: contact.source || 'external'
      }));
      
      // Combine all contacts
      const allContacts = [...executivesAsContacts, ...externalContacts];
      setContacts(allContacts);
      
    } catch (err) {
      setError('Failed to load contacts: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const filterContacts = () => {
    let filtered = [...contacts];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(contact => 
        contact.full_name.toLowerCase().includes(term) ||
        contact.email?.toLowerCase().includes(term) ||
        contact.company?.toLowerCase().includes(term) ||
        contact.title?.toLowerCase().includes(term) ||
        contact.primary_phone?.includes(term)
      );
    }

    // Source filter only (type filter removed)

    // Source filter (executive vs external)
    if (selectedSource !== 'all') {
      filtered = filtered.filter(contact => contact.source === selectedSource);
    }

    setFilteredContacts(filtered);
  };

  const handleAddContact = (newContact) => {
    setContacts([...contacts, newContact]);
    setShowAddModal(false);
  };

  const handleEditContact = (contact) => {
    setSelectedContact(contact);
    setShowEditModal(true);
  };

  const handleUpdateContact = (updatedContact) => {
    setContacts(contacts.map(c => 
      c.id === updatedContact.id ? updatedContact : c
    ));
    setShowEditModal(false);
    setSelectedContact(null);
  };

  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);

  const handleDeleteContact = async (contactId) => {
    // Defensive check for valid ID
    if (!contactId) {
      setError('Invalid contact ID - cannot delete');
      return;
    }

    // Set up for deletion and show custom confirmation modal
    setContactToDelete(contactId);
    setShowDeleteConfirm(true);
  };

  const confirmContactDelete = async () => {
    if (!contactToDelete) return;

    try {
      await axios.delete(`/external-contacts/${contactToDelete}`);

      // Filter out the deleted contact
      const updatedContacts = contacts.filter(contact => contact.id !== contactToDelete);

      setContacts(updatedContacts);
      setError('');
      
      // Show success message
      const successMessage = document.createElement('div');
      successMessage.className = 'fixed top-4 right-4 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-md shadow-lg z-50';
      successMessage.textContent = 'Contact deleted successfully';
      document.body.appendChild(successMessage);
      
      // Remove success message after 3 seconds
      setTimeout(() => {
        if (document.body.contains(successMessage)) {
          document.body.removeChild(successMessage);
        }
      }, 3000);
      
    } catch (error) {
      console.error('Delete contact error:', error);
      let errorMessage = 'Failed to delete contact';
      
      if (error.response?.status === 403) {
        errorMessage = 'Permission denied. You can only delete contacts you created, or you need admin privileges.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Contact not found or already deleted.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      
      setError(errorMessage);
    } finally {
      setShowDeleteConfirm(false);
      setContactToDelete(null);
    }
  };

  const cancelContactDelete = () => {
    setShowDeleteConfirm(false);
    setContactToDelete(null);
  };

  const getContactTypeLabel = (type) => {
    const types = {
      client: 'Client',
      prospect: 'Prospect',
      business_partner: 'Business Partner',
      affiliate: 'Affiliate',
      private: 'Private'
    };
    return types[type] || type;
  };

  const getContactTypeColor = (type) => {
    const colors = {
      client: 'bg-blue-100 text-blue-800 border-blue-200',
      prospect: 'bg-green-100 text-green-800 border-green-200',
      business_partner: 'bg-purple-100 text-purple-800 border-purple-200',
      affiliate: 'bg-orange-100 text-orange-800 border-orange-200',
      private: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors[type] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const canDeleteContact = (contact) => {
    if (!user || contact.source !== 'external') {
      return false;
    }
    
    // Superadmins and admins can delete any contact
    if (user.role === 'superadmin' || user.role === 'admin') {
      return true;
    }
    
    // Contact creators can delete their own contacts
    return contact.created_by === user.user_id;
  };

  const canEditContact = (contact) => {
    // Show edit button for all external contacts
    // Backend will handle actual permission checking
    return contact.source === 'external';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Global Contacts</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage your organization's contacts and external relationships
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add External Contact
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search contacts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Source Filter */}
          <select
            value={selectedSource}
            onChange={(e) => setSelectedSource(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Sources</option>
            <option value="executive">Executives</option>
            <option value="external">External Contacts</option>
          </select>
        </div>
      </div>

      {/* Contact List */}
      {filteredContacts.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-12 text-center">
          <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No contacts found</h3>
          <p className="text-gray-500 mb-4">
            {searchTerm || selectedSource !== 'all'
              ? 'Try adjusting your filters or search terms.'
              : 'Get started by adding your first external contact.'
            }
          </p>
          {!searchTerm && selectedSource === 'all' && (
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
            >
              Add External Contact
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="divide-y divide-gray-200">
            {filteredContacts.map((contact) => (
              <div key={contact.id} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1">
                    {/* Avatar */}
                    <div className="flex-shrink-0">
                      {contact.profile_photo_url ? (
                        <img
                          src={contact.profile_photo_url}
                          alt={contact.full_name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-700">
                            {getInitials(contact.first_name, contact.last_name)}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Contact Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-lg font-semibold text-gray-900">
                              {contact.full_name}
                            </h3>
                            
                            {/* Source Badge */}
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              contact.source === 'executive' 
                                ? 'bg-blue-100 text-blue-800 border border-blue-200' 
                                : 'bg-gray-100 text-gray-800 border border-gray-200'
                            }`}>
                              {contact.source === 'executive' ? 'Executive' : 'External'}
                            </span>

                            {/* Contact Type Badge (for external contacts) */}
                            {contact.contact_type && (
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getContactTypeColor(contact.contact_type)}`}>
                                {getContactTypeLabel(contact.contact_type)}
                              </span>
                            )}
                          </div>

                          {/* Title and Company */}
                          {(contact.title || contact.company) && (
                            <p className="text-sm text-gray-600 mb-2">
                              {[contact.title, contact.company].filter(Boolean).join(' at ')}
                            </p>
                          )}

                          {/* Contact Details */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-500">
                            {contact.email && (
                              <div className="flex items-center gap-2">
                                <Mail className="w-4 h-4" />
                                <a href={`mailto:${contact.email}`} className="text-blue-600 hover:text-blue-800">
                                  {contact.email}
                                </a>
                              </div>
                            )}
                            
                            {contact.primary_phone && (
                              <div className="flex items-center gap-2">
                                <Phone className="w-4 h-4" />
                                <a href={`tel:${contact.primary_phone}`} className="text-blue-600 hover:text-blue-800">
                                  {contact.primary_phone}
                                </a>
                              </div>
                            )}

                            {contact.linkedin_profile && (
                              <div className="flex items-center gap-2">
                                <ExternalLink className="w-4 h-4" />
                                <a 
                                  href={contact.linkedin_profile} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-800"
                                >
                                  LinkedIn
                                </a>
                              </div>
                            )}

                            {contact.primary_contact_name && (
                              <div className="flex items-center gap-2">
                                <User className="w-4 h-4" />
                                <span>Primary: {contact.primary_contact_name}</span>
                              </div>
                            )}
                          </div>

                          {/* Executive Assistant Info */}
                          {(contact.assistant_name || contact.assistant_phone || contact.assistant_email) && (
                            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                              <div className="flex items-center gap-2 mb-2">
                                <Briefcase className="w-4 h-4 text-blue-600" />
                                <span className="text-sm font-medium text-blue-800">Executive Assistant</span>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm text-blue-700">
                                {contact.assistant_name && (
                                  <div className="flex items-center gap-1">
                                    <User className="w-3 h-3" />
                                    <span>{contact.assistant_name}</span>
                                  </div>
                                )}
                                {contact.assistant_phone && (
                                  <div className="flex items-center gap-1">
                                    <Phone className="w-3 h-3" />
                                    <a href={`tel:${contact.assistant_phone}`} className="text-blue-600 hover:text-blue-800">
                                      {contact.assistant_phone}
                                    </a>
                                  </div>
                                )}
                                {contact.assistant_email && (
                                  <div className="flex items-center gap-1">
                                    <Mail className="w-3 h-3" />
                                    <a href={`mailto:${contact.assistant_email}`} className="text-blue-600 hover:text-blue-800">
                                      {contact.assistant_email}
                                    </a>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Internal Comments */}
                          {contact.internal_comments && (
                            <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm text-gray-700">
                              <strong>Internal Notes:</strong> {contact.internal_comments}
                            </div>
                          )}

                          {/* Confidential Notes */}
                          {contact.can_view_confidential && contact.confidential_notes && (
                            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-gray-700">
                              <strong>Confidential:</strong> {contact.confidential_notes}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex-shrink-0 flex items-center gap-2 ml-4">
                    {/* Edit Button */}
                    <button
                      onClick={() => handleEditContact(contact)}
                      className="p-2 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors border border-blue-200"
                      title="Edit Contact"
                    >
                      <Edit3 className="w-5 h-5 text-blue-600" />
                    </button>

                    {/* Delete Button - only for external contacts */}
                    {contact.source === 'external' && (
                      <button
                        onClick={() => handleDeleteContact(contact.id)}
                        className="p-2 bg-red-50 hover:bg-red-100 rounded-md transition-colors border border-red-200"
                        title="Delete Contact"
                      >
                        <Trash2 className="w-5 h-5 text-red-500" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add Contact Modal */}
      {showAddModal && (
        <AddExternalContactModal
          onClose={() => setShowAddModal(false)}
          onSave={handleAddContact}
        />
      )}

      {/* Edit Contact Modal */}
      {showEditModal && selectedContact && (
        <EditExternalContactModal
          contact={selectedContact}
          onClose={() => {
            setShowEditModal(false);
            setSelectedContact(null);
          }}
          onSave={handleUpdateContact}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 h-full w-full z-[70] flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.962-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900">Delete Contact</h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Are you sure you want to delete this contact? This action cannot be undone.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={confirmContactDelete}
                >
                  Delete
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
                  onClick={cancelContactDelete}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactList;